/** Leave (`lv.*`, NX-421) — English. Shape checked against `leave.ts`. */
import type { lvPl, Shape } from './leave';

export const lvEn: Shape<typeof lvPl> = {
  nav: 'Leave',
  title: 'Leave',
  description: 'Who is on leave, team balances from InsERT and the NEXOPS schedule, calendar, year timeline and requests to decide.',
  close: 'Close',
  moved: {
    title: 'Leave has its own view',
    body: 'Entitlement and carried-over leave, balances from InsERT and the schedule, the calendar and requests to decide are in the Leave view.',
    noAccess: 'HR and managers keep leave entitlement and balances in the Leave view.',
    open: 'Open Leave',
  },
  days: '{value} days',
  hours: '{value} h',
  year: { prev: 'Previous year', next: 'Next year', label: 'Year' },
  hero: {
    people: 'People',
    over: 'Over the pool',
    lowHint: 'few days left: {n}',
    used: 'Days used',
    plannedHint: 'planned: {n}',
    utilization: 'Pool used',
    pending: 'Requests to decide',
    today: 'On leave today',
    peakHint: 'peak: {day} ({n} people)',
    elapsedHint: '{pct} of the year gone',
    left: 'Days left to take',
    manyHint: '{n} people have 10+ days',
  },
  insight: {
    pending: 'Leave requests waiting for your decision: {n}.',
    pendingAction: 'Review',
    carriedLate: '{n} people did not use their carried-over leave from {year} by the deadline (30 September) — {days} days in total.',
    show: 'Show',
    peak: 'On leave today: {n}. Most people in the next 60 days: {day} ({k}).',
    calendar: 'Calendar',
  },
  sort: { label: 'Sort', name: 'By surname', remaining: 'Most days left', used: 'Most days used' },
  ov: {
    people: '{n} people',
    erpOnly: 'from InsERT',
    today: {
      title: 'On leave today',
      empty: 'Nobody is on leave today',
      back: 'back',
      next: 'Next working day ({day}): {n} on leave',
    },
    months: {
      title: 'Leave by month, {year}',
      description: '{total} person-days of leave in the year · busiest: {peak}',
      empty: 'No leave this year',
      total: 'Person-days of leave',
      perDay: 'on average {n} people on leave per day',
      future: 'leave already planned',
      hint: 'Working days, each person once. Click a month to open the calendar.',
    },
    pool: {
      title: 'Team leave pool',
      description: '{n} people with entitlement from InsERT or NEXOPS · {days} days in total',
      usedOfPool: 'of the pool used',
      elapsed: '{pct} of the year gone',
      used: 'Used',
      planned: 'Planned',
      left: 'Left to take',
      pace: {
        slow: 'The team takes leave more slowly than the year passes.',
        ok: 'Leave is being taken in step with the year.',
        fast: 'The team takes leave faster than the year passes.',
      },
      projection: 'At this pace about {days} days will carry over at year end.',
      over: 'Over the pool: {days} days for {n} people.',
    },
    carried: {
      title: 'Carried-over leave from {year}',
      description: 'To be granted by 30 September {year} (Labour Code art. 168)',
      empty: 'Carried-over leave used on time',
      late: 'Past the deadline: {n} people did not use {days} days of carried-over leave.',
      soon: '{left} days to the deadline: {n} people still have {days} days of carried-over leave.',
      row: 'carried over {carried} · used by 30.09: {used}',
    },
    busiest: {
      title: 'Busiest days',
      description: 'Most people on leave in the next 60 days',
      empty: 'No leave in the next 60 days',
    },
    upcoming: {
      title: 'Upcoming leave',
      description: 'Leave starting in the next 60 days',
      thisWeek: 'This week',
      nextWeek: 'Next week',
      later: 'Later',
      none: 'None',
      more: 'and {n} more',
    },
    leaders: {
      title: 'Most days left to take',
      description: '{n} people have at least {days} days of leave left',
      empty: 'Nobody has leave left',
    },
  },
  erp: {
    status: '{employees} employees from Gratyfikant, {linked} linked to accounts · last import: {at}',
    never: 'not yet',
    toLink: '{n} to link',
    short: 'InsERT: {employees} employees ({linked} with an account) · import {at}',
  },
  tabs: { overview: 'Overview', team: 'Team', calendar: 'Calendar', timeline: 'Year timeline', requests: 'Requests', link: 'To link' },
  source: { MANUAL: 'Manual', ERP: 'InsERT', DEFAULT: 'Default', NEXOPS: 'NEXOPS', REQUEST: 'Request' },
  sourceHint: {
    MANUAL: 'Entitlement entered manually in NEXOPS — overrides InsERT.',
    ERP: 'Entitlement and carried-over leave from Gratyfikant (InsERT).',
    DEFAULT: 'Default entitlement of the organization — nothing in NEXOPS or InsERT.',
  },
  status: { over: 'Over the pool', low: 'Few left', ok: 'OK' },
  account: { label: 'Account', withData: 'With data', all: 'Everyone', account: 'With account', erpOnly: 'InsERT only' },
  group: { label: 'Grouping', none: 'No groups', status: 'By balance', department: 'By department' },
  team: {
    search: 'Search person',
    allDepartments: 'All departments',
    allStatuses: 'Any balance',
    noDepartment: 'No department',
    person: 'Person',
    source: 'Entitlement from',
    entitlement: 'Entitlement',
    carried: 'Carried over',
    used: 'Used',
    planned: 'Planned',
    remaining: 'Remaining',
    edit: 'Entitlement',
    empty: 'Nobody matches the filters',
    erpOnly: 'InsERT only — no NEXOPS account',
    usedSplit: 'From InsERT: {erp} · from NEXOPS: {nexops} · in both (counted once): {both}',
    rule: 'Entitlement: manual in NEXOPS > InsERT > organization default ({days} days). Used = working days of leave from InsERT and the NEXOPS schedule — the same day counts once. 1 day of entitlement = 8 h; part-time, a day of leave uses the person’s daily norm from time tracking.',
    link: { AUTO: 'linked automatically', MANUAL: 'linked manually' },
    away: 'on leave · back {day}',
    awayHint: 'On leave now — back {day}',
  },
  upcoming: { title: 'Upcoming leave (60 days)', empty: 'No leave in the next 60 days', days: 'working days: {n}' },
  entitlement: {
    title: 'Leave entitlement — {name}, {year}',
    description: 'A manual entry overrides InsERT and the default. Days of 8 h (Polish Labour Code art. 154²).',
    current: 'Now:',
    currentValue: 'entitlement {ent}, carried over {carried}',
    days: 'Entitlement (days)',
    carried: 'Carried over from last year (days)',
    save: 'Save manually',
    cancel: 'Cancel',
    reset: 'Restore InsERT / default',
    resetHint: 'Removes the manual entry — InsERT or the organization default applies again.',
    saved: 'Leave entitlement saved',
    cleared: 'InsERT / default entitlement restored',
    failed: 'Could not save the entitlement',
    invalid: 'Enter days from 0 to {max} (step 0.1).',
    normHint: 'Daily norm of the person: {h} h (time tracking → profile). Days off left = pool in hours ÷ norm.',
  },
  person: {
    subtitle: 'Leave in {year}',
    norm: 'norm {h} h a day',
    months: 'Leave days per month (InsERT / NEXOPS)',
    history: 'Leave in the year',
    empty: 'No leave this year',
    onDemand: 'on demand',
    nowOn: 'On leave now — back {day}',
    next: 'Next leave: {from} – {to} (working days: {n})',
  },
  calendar: {
    monthTotal: 'Person-days of leave: {n}',
    prev: 'Previous month',
    next: 'Next month',
    coverage: 'Highlight days from people',
    holiday: 'Public holiday',
    nobody: 'Nobody is on leave that day',
    pickDay: 'Click a day to see who is on leave.',
    today: 'Today',
    less: 'fewer',
    more: 'more',
    dayTitle: 'On leave: {n}',
    onLeave: 'On leave: {n}',
    back: 'back {day}',
  },
  timeline: { empty: 'Nobody is on leave this year', total: 'Working days of leave in the year', sort: { first: 'By date', name: 'By surname', days: 'By days' } },
  requests: {
    mode: 'Requests view',
    toDecide: 'To decide',
    history: 'History',
    allStatuses: 'Any status',
    allTypes: 'Any type',
    wholeYear: 'All of {year}',
    empty: 'No requests for these filters',
    emptyToDecide: 'Nothing waits for your decision',
    workingDays: 'working days: {n}',
    exceeds: 'Over the leave balance',
    before: 'Balance before',
    thisRequest: 'This request',
    after: 'Balance after',
    collisions: 'Other leave in the team at that time',
    noCollisions: 'Nobody in the team is on leave then',
    response: 'Response',
    respondedBy: 'Decided by: {name}',
    notYours: 'Someone else decides this request under the organization setting.',
    comment: 'Decision comment',
    commentPh: 'Comment (optional) — goes to the employee and to the printout',
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Request approved',
    rejected: 'Request rejected',
    failed: 'Could not save the decision',
  },
  link: {
    description:
      'Employees from Gratyfikant (name, entitlement and dates of annual leave — no sick leave or other absences). The import links them to accounts when the name is unambiguous; link the rest here. An employee unlinked by hand is not linked again by the import.',
    show: 'Show',
    filter: { unlinked: 'No account', linked: 'Linked', all: 'Everyone' },
    empty: 'Nobody matches the filters',
    noErp: 'No InsERT data — run the “Employee leave” import in Integrations.',
    employee: 'Employee (InsERT)',
    years: 'Years with data',
    account: 'NEXOPS account',
    mode: { AUTO: 'automatically', MANUAL: 'manually' },
    pick: 'Pick an account…',
    linkBtn: 'Link',
    unlink: 'Unlink',
    linked: 'Employee linked to the account',
    unlinked: 'Unlinked — the import will not link it automatically',
    failed: 'Could not change the link',
  },
};
